import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { lessonBuilderAPI } from '../api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Save, Eye, Trash2, GripVertical, 
  Star, Leaf, Palette, Flame, Droplets, BookOpen,
  MessageCircle, HelpCircle, Lightbulb, Trophy, Sparkles
} from 'lucide-react';

// Realm icons
const REALM_ICONS = {
  star_map: Star,
  memory_garden: Leaf,
  creative_forge: Palette,
  radiant_will: Flame,
  calm_cove: Droplets
};

// Block type icons
const BLOCK_ICONS = {
  discovery: Lightbulb,
  guided_practice: BookOpen,
  reinforcement: MessageCircle,
  challenge: Trophy,
  creative: Sparkles
};

// Interaction types per block type
const INTERACTION_TYPES = {
  discovery: [
    { value: 'story_narration', label: 'Story/Narration', description: 'Tell a story or explain a concept' },
    { value: 'image_showcase', label: 'Image Showcase', description: 'Display images with descriptions' },
    { value: 'concept_intro', label: 'Concept Introduction', description: 'Introduce a new idea step by step' },
    { value: 'video_content', label: 'Video Content', description: 'Embed video explanation' },
  ],
  guided_practice: [
    { value: 'multiple_choice', label: 'Multiple Choice', description: 'Select the correct answer' },
    { value: 'matching_pairs', label: 'Matching Pairs', description: 'Match items from two columns' },
    { value: 'drag_drop_order', label: 'Drag & Drop Order', description: 'Arrange items in correct sequence' },
    { value: 'true_false', label: 'True/False', description: 'Decide if statement is true or false' },
  ],
  reinforcement: [
    { value: 'fill_blank', label: 'Fill in the Blank', description: 'Complete the sentence' },
    { value: 'sorting', label: 'Sorting/Categorizing', description: 'Sort items into categories' },
    { value: 'quick_recall', label: 'Quick Recall', description: 'Short answer recall questions' },
    { value: 'sequence_order', label: 'Sequence Order', description: 'Put steps in correct order' },
  ],
  challenge: [
    { value: 'problem_solving', label: 'Problem Solving', description: 'Solve a scenario-based problem' },
    { value: 'logic_puzzle', label: 'Logic Puzzle', description: 'Use reasoning to find the answer' },
    { value: 'multi_step', label: 'Multi-Step Question', description: 'Complete connected questions' },
    { value: 'open_response', label: 'Open Response', description: 'Write a detailed answer' },
  ],
  creative: [
    { value: 'drawing', label: 'Drawing Activity', description: 'Draw or illustrate something' },
    { value: 'writing', label: 'Creative Writing', description: 'Write a story, poem, or description' },
    { value: 'building', label: 'Building Activity', description: 'Create or construct something' },
    { value: 'music_rhythm', label: 'Music/Rhythm', description: 'Create beats or musical patterns' },
    { value: 'reflection', label: 'Reflection', description: 'Think and share personal thoughts' },
  ],
};

// Enhanced Question block component with varied interaction types
const QuestionBlock = ({ block, onChange, onRemove, blockType }) => {
  const [expanded, setExpanded] = useState(true);
  const Icon = BLOCK_ICONS[blockType] || BookOpen;
  const interactionTypes = INTERACTION_TYPES[blockType] || [];
  
  // Get the current interaction type (with defaults)
  const currentInteractionType = block.interaction_type || 
    (blockType === 'discovery' ? 'story_narration' : 
     blockType === 'guided_practice' ? 'multiple_choice' :
     blockType === 'reinforcement' ? 'fill_blank' :
     blockType === 'challenge' ? 'problem_solving' :
     blockType === 'creative' ? 'writing' : 'multiple_choice');
  
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mb-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-slate-500 cursor-move" />
          <Icon className="w-4 h-4 text-indigo-400" />
          <span className="text-sm font-medium text-slate-300 capitalize">{blockType.replace('_', ' ')}</span>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setExpanded(!expanded)}
            className="text-slate-400 hover:text-white text-sm"
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
          {blockType !== 'discovery' && (
            <button 
              onClick={onRemove}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {expanded && (
        <div className="space-y-3">
          {/* Interaction Type Selector (for all block types) */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Interaction Type</label>
            <select
              value={currentInteractionType}
              onChange={(e) => onChange({ ...block, interaction_type: e.target.value })}
              className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm"
            >
              {interactionTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              {interactionTypes.find(t => t.value === currentInteractionType)?.description}
            </p>
          </div>
          
          {/* === DISCOVERY BLOCK CONTENT === */}
          {blockType === 'discovery' && (
            <>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Aret'Hera Introduction</label>
                <textarea
                  value={block.arethera_intro || ''}
                  onChange={(e) => onChange({ ...block, arethera_intro: e.target.value })}
                  placeholder="Welcome, young explorer. Today we will discover..."
                  className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                  rows={2}
                />
              </div>
              
              {currentInteractionType === 'story_narration' && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Story Content</label>
                  <textarea
                    value={block.content || ''}
                    onChange={(e) => onChange({ ...block, content: e.target.value })}
                    placeholder="Once upon a time... (Write the narrative here)"
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                    rows={6}
                  />
                </div>
              )}
              
              {currentInteractionType === 'image_showcase' && (
                <>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Image URL</label>
                    <input
                      type="text"
                      value={block.image_url || ''}
                      onChange={(e) => onChange({ ...block, image_url: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Image Description</label>
                    <textarea
                      value={block.content || ''}
                      onChange={(e) => onChange({ ...block, content: e.target.value })}
                      placeholder="Describe what the image shows..."
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                      rows={3}
                    />
                  </div>
                </>
              )}
              
              {currentInteractionType === 'concept_intro' && (
                <>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Concept Title</label>
                    <input
                      type="text"
                      value={block.concept_title || ''}
                      onChange={(e) => onChange({ ...block, concept_title: e.target.value })}
                      placeholder="What is Gravity?"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Explanation Steps (one per line)</label>
                    <textarea
                      value={(block.steps || []).join('\n')}
                      onChange={(e) => onChange({ ...block, steps: e.target.value.split('\n').filter(s => s.trim()) })}
                      placeholder="Step 1: First, let's understand...&#10;Step 2: Now let's see how...&#10;Step 3: Finally..."
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                      rows={5}
                    />
                  </div>
                </>
              )}
              
              {currentInteractionType === 'video_content' && (
                <>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Video URL (YouTube or embed)</label>
                    <input
                      type="text"
                      value={block.video_url || ''}
                      onChange={(e) => onChange({ ...block, video_url: e.target.value })}
                      placeholder="https://youtube.com/watch?v=..."
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Video Summary</label>
                    <textarea
                      value={block.content || ''}
                      onChange={(e) => onChange({ ...block, content: e.target.value })}
                      placeholder="What will the child learn from this video?"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                      rows={2}
                    />
                  </div>
                </>
              )}
            </>
          )}
          
          {/* === GUIDED PRACTICE BLOCK CONTENT === */}
          {blockType === 'guided_practice' && (
            <>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Question/Instruction</label>
                <textarea
                  value={block.content || ''}
                  onChange={(e) => onChange({ ...block, content: e.target.value })}
                  placeholder={
                    currentInteractionType === 'multiple_choice' ? "What is the question?" :
                    currentInteractionType === 'matching_pairs' ? "Match the items in Column A with Column B" :
                    currentInteractionType === 'drag_drop_order' ? "Arrange these items in the correct order" :
                    "Is this statement true or false?"
                  }
                  className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                  rows={2}
                />
              </div>
              
              {currentInteractionType === 'multiple_choice' && (
                <>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Answer Options (one per line)</label>
                    <textarea
                      value={(block.options || []).join('\n')}
                      onChange={(e) => onChange({ ...block, options: e.target.value.split('\n').filter(o => o.trim()) })}
                      placeholder="Option A&#10;Option B&#10;Option C&#10;Option D"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Correct Answer</label>
                    <input
                      type="text"
                      value={block.correct_answer || ''}
                      onChange={(e) => onChange({ ...block, correct_answer: e.target.value })}
                      placeholder="Enter the correct option"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500"
                    />
                  </div>
                </>
              )}
              
              {currentInteractionType === 'matching_pairs' && (
                <>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Column A Items (one per line)</label>
                    <textarea
                      value={(block.column_a || []).join('\n')}
                      onChange={(e) => onChange({ ...block, column_a: e.target.value.split('\n').filter(o => o.trim()) })}
                      placeholder="Apple&#10;Dog&#10;Sun"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Column B Items (matching order)</label>
                    <textarea
                      value={(block.column_b || []).join('\n')}
                      onChange={(e) => onChange({ ...block, column_b: e.target.value.split('\n').filter(o => o.trim()) })}
                      placeholder="Fruit&#10;Animal&#10;Star"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                      rows={3}
                    />
                  </div>
                </>
              )}
              
              {currentInteractionType === 'drag_drop_order' && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Items to Order (in correct sequence)</label>
                  <textarea
                    value={(block.items_to_order || []).join('\n')}
                    onChange={(e) => onChange({ ...block, items_to_order: e.target.value.split('\n').filter(o => o.trim()) })}
                    placeholder="First step&#10;Second step&#10;Third step&#10;Fourth step"
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                    rows={4}
                  />
                  <p className="text-xs text-slate-500 mt-1">Enter items in the correct order. They will be shuffled for the child.</p>
                </div>
              )}
              
              {currentInteractionType === 'true_false' && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Correct Answer</label>
                  <select
                    value={block.correct_answer || 'true'}
                    onChange={(e) => onChange({ ...block, correct_answer: e.target.value })}
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm"
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                </div>
              )}
              
              {/* Hint and Explanation for guided practice */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Hint (shown on 2nd error)</label>
                <input
                  type="text"
                  value={block.hint || ''}
                  onChange={(e) => onChange({ ...block, hint: e.target.value })}
                  placeholder="A helpful hint..."
                  className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Explanation (shown on 3rd error)</label>
                <textarea
                  value={block.explanation || ''}
                  onChange={(e) => onChange({ ...block, explanation: e.target.value })}
                  placeholder="Full explanation of the answer..."
                  className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                  rows={2}
                />
              </div>
            </>
          )}
          
          {/* === REINFORCEMENT BLOCK CONTENT === */}
          {blockType === 'reinforcement' && (
            <>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">
                  {currentInteractionType === 'fill_blank' ? 'Sentence (use ___ for blank)' :
                   currentInteractionType === 'sorting' ? 'Sorting Instructions' :
                   currentInteractionType === 'quick_recall' ? 'Recall Question' :
                   'Sequence Description'}
                </label>
                <textarea
                  value={block.content || ''}
                  onChange={(e) => onChange({ ...block, content: e.target.value })}
                  placeholder={
                    currentInteractionType === 'fill_blank' ? "The ___ orbits around the Sun." :
                    currentInteractionType === 'sorting' ? "Sort these items into the correct categories" :
                    currentInteractionType === 'quick_recall' ? "What did we learn about...?" :
                    "Put these steps in the correct order"
                  }
                  className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                  rows={2}
                />
              </div>
              
              {currentInteractionType === 'fill_blank' && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Correct Answer(s) for Blank (comma separated)</label>
                  <input
                    type="text"
                    value={block.correct_answer || ''}
                    onChange={(e) => onChange({ ...block, correct_answer: e.target.value })}
                    placeholder="Earth, earth"
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500"
                  />
                </div>
              )}
              
              {currentInteractionType === 'sorting' && (
                <>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Categories (one per line)</label>
                    <textarea
                      value={(block.categories || []).join('\n')}
                      onChange={(e) => onChange({ ...block, categories: e.target.value.split('\n').filter(c => c.trim()) })}
                      placeholder="Living Things&#10;Non-Living Things"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Items with Categories (format: Item | Category)</label>
                    <textarea
                      value={(block.sorting_items || []).map(i => `${i.item} | ${i.category}`).join('\n')}
                      onChange={(e) => {
                        const items = e.target.value.split('\n').filter(l => l.trim()).map(line => {
                          const [item, category] = line.split('|').map(s => s.trim());
                          return { item: item || '', category: category || '' };
                        });
                        onChange({ ...block, sorting_items: items });
                      }}
                      placeholder="Dog | Living Things&#10;Rock | Non-Living Things&#10;Tree | Living Things"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                      rows={4}
                    />
                  </div>
                </>
              )}
              
              {(currentInteractionType === 'quick_recall' || currentInteractionType === 'sequence_order') && (
                <>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">
                      {currentInteractionType === 'quick_recall' ? 'Acceptable Answers (one per line)' : 'Steps in Correct Order'}
                    </label>
                    <textarea
                      value={(block.options || []).join('\n')}
                      onChange={(e) => onChange({ ...block, options: e.target.value.split('\n').filter(o => o.trim()) })}
                      placeholder={currentInteractionType === 'quick_recall' ? "Gravity&#10;gravity&#10;The force of gravity" : "Step 1&#10;Step 2&#10;Step 3"}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                      rows={4}
                    />
                  </div>
                </>
              )}
            </>
          )}
          
          {/* === CHALLENGE BLOCK CONTENT === */}
          {blockType === 'challenge' && (
            <>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">
                  {currentInteractionType === 'problem_solving' ? 'Scenario Description' :
                   currentInteractionType === 'logic_puzzle' ? 'Puzzle Setup' :
                   currentInteractionType === 'multi_step' ? 'Challenge Introduction' :
                   'Question Prompt'}
                </label>
                <textarea
                  value={block.content || ''}
                  onChange={(e) => onChange({ ...block, content: e.target.value })}
                  placeholder={
                    currentInteractionType === 'problem_solving' ? "You're a scientist trying to..." :
                    currentInteractionType === 'logic_puzzle' ? "Look at the clues and figure out..." :
                    currentInteractionType === 'multi_step' ? "This challenge has multiple parts..." :
                    "Think carefully and answer..."
                  }
                  className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                  rows={4}
                />
              </div>
              
              {(currentInteractionType === 'problem_solving' || currentInteractionType === 'logic_puzzle') && (
                <>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Possible Solutions (one per line)</label>
                    <textarea
                      value={(block.options || []).join('\n')}
                      onChange={(e) => onChange({ ...block, options: e.target.value.split('\n').filter(o => o.trim()) })}
                      placeholder="Solution A&#10;Solution B&#10;Solution C"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Correct Solution</label>
                    <input
                      type="text"
                      value={block.correct_answer || ''}
                      onChange={(e) => onChange({ ...block, correct_answer: e.target.value })}
                      placeholder="The correct solution"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500"
                    />
                  </div>
                </>
              )}
              
              {currentInteractionType === 'multi_step' && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Steps/Sub-questions (format: Question | Answer)</label>
                  <textarea
                    value={(block.steps || []).map(s => `${s.question} | ${s.answer}`).join('\n')}
                    onChange={(e) => {
                      const steps = e.target.value.split('\n').filter(l => l.trim()).map(line => {
                        const [question, answer] = line.split('|').map(s => s.trim());
                        return { question: question || '', answer: answer || '' };
                      });
                      onChange({ ...block, steps: steps });
                    }}
                    placeholder="What is 2 + 2? | 4&#10;Now multiply by 3 | 12&#10;Finally subtract 2 | 10"
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                    rows={4}
                  />
                </div>
              )}
              
              {currentInteractionType === 'open_response' && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Sample/Expected Response Keywords</label>
                  <textarea
                    value={block.expected_keywords || ''}
                    onChange={(e) => onChange({ ...block, expected_keywords: e.target.value })}
                    placeholder="Keywords to look for in the response (comma separated)"
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                    rows={2}
                  />
                </div>
              )}
            </>
          )}
          
          {/* === CREATIVE BLOCK CONTENT === */}
          {blockType === 'creative' && (
            <>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Activity Prompt</label>
                <textarea
                  value={block.content || ''}
                  onChange={(e) => onChange({ ...block, content: e.target.value })}
                  placeholder={
                    currentInteractionType === 'drawing' ? "Draw a picture of..." :
                    currentInteractionType === 'writing' ? "Write a short story about..." :
                    currentInteractionType === 'building' ? "Create something that..." :
                    currentInteractionType === 'music_rhythm' ? "Make a rhythm pattern that..." :
                    "Think about what you learned and share..."
                  }
                  className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                  rows={3}
                />
              </div>
              
              {(currentInteractionType === 'drawing' || currentInteractionType === 'building') && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Inspiration/Example Image URL (optional)</label>
                  <input
                    type="text"
                    value={block.example_image || ''}
                    onChange={(e) => onChange({ ...block, example_image: e.target.value })}
                    placeholder="https://example.com/inspiration.jpg"
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500"
                  />
                </div>
              )}
              
              {currentInteractionType === 'writing' && (
                <>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Writing Type</label>
                    <select
                      value={block.writing_type || 'story'}
                      onChange={(e) => onChange({ ...block, writing_type: e.target.value })}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm"
                    >
                      <option value="story">Short Story</option>
                      <option value="poem">Poem</option>
                      <option value="description">Description</option>
                      <option value="letter">Letter</option>
                      <option value="journal">Journal Entry</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Minimum Word Count (optional)</label>
                    <input
                      type="number"
                      value={block.min_words || ''}
                      onChange={(e) => onChange({ ...block, min_words: parseInt(e.target.value) || null })}
                      placeholder="20"
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500"
                    />
                  </div>
                </>
              )}
              
              {currentInteractionType === 'music_rhythm' && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Beat Pattern Hint (optional)</label>
                  <input
                    type="text"
                    value={block.beat_hint || ''}
                    onChange={(e) => onChange({ ...block, beat_hint: e.target.value })}
                    placeholder="Try: BOOM tap tap BOOM tap"
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500"
                  />
                </div>
              )}
              
              {currentInteractionType === 'reflection' && (
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Guiding Questions</label>
                  <textarea
                    value={(block.guiding_questions || []).join('\n')}
                    onChange={(e) => onChange({ ...block, guiding_questions: e.target.value.split('\n').filter(q => q.trim()) })}
                    placeholder="What did you find most interesting?&#10;How does this connect to your life?&#10;What would you like to learn more about?"
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2 text-white text-sm placeholder:text-slate-500 resize-none"
                    rows={3}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const LessonBuilderPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [realms, setRealms] = useState({});
  const [skillTags, setSkillTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  // Lesson state
  const [lesson, setLesson] = useState({
    title: '',
    description: '',
    realm: 'star_map',
    skill_tags: [],
    target_duration_minutes: 5,
    discovery_block: { arethera_intro: '', content: '' },
    guided_practice_blocks: [],
    reinforcement_blocks: [],
    challenge_block: null,
    creative_block: null,
    supports_early_explorer: true,
    supports_pathfinder: true,
    supports_astronomer: true
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const [realmsRes, templatesRes] = await Promise.all([
        lessonBuilderAPI.getRealms(),
        lessonBuilderAPI.getTemplates()
      ]);
      setRealms(realmsRes.data.realms || {});
      setSkillTags(realmsRes.data.skill_tags || []);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load lesson builder config');
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (template) => {
    const newLesson = { ...lesson };
    newLesson.guided_practice_blocks = [];
    newLesson.reinforcement_blocks = [];
    newLesson.challenge_block = null;
    newLesson.creative_block = null;
    
    template.structure.forEach((blockType) => {
      if (blockType === 'guided_practice') {
        newLesson.guided_practice_blocks.push({ content: '', question_type: 'multiple_choice', options: [], correct_answer: '', hint: '', explanation: '' });
      } else if (blockType === 'reinforcement') {
        newLesson.reinforcement_blocks.push({ content: '', question_type: 'multiple_choice', options: [], correct_answer: '' });
      } else if (blockType === 'challenge') {
        newLesson.challenge_block = { content: '', question_type: 'multiple_choice', options: [], correct_answer: '' };
      } else if (blockType === 'creative') {
        newLesson.creative_block = { content: '' };
      }
    });
    
    setLesson(newLesson);
    toast.success(`Applied "${template.name}" template`);
  };

  const addBlock = (type) => {
    if (type === 'guided_practice') {
      setLesson(prev => ({
        ...prev,
        guided_practice_blocks: [...prev.guided_practice_blocks, { content: '', question_type: 'multiple_choice', options: [], correct_answer: '', hint: '', explanation: '' }]
      }));
    } else if (type === 'reinforcement') {
      setLesson(prev => ({
        ...prev,
        reinforcement_blocks: [...prev.reinforcement_blocks, { content: '', question_type: 'multiple_choice', options: [], correct_answer: '' }]
      }));
    } else if (type === 'challenge') {
      setLesson(prev => ({ ...prev, challenge_block: { content: '', question_type: 'multiple_choice', options: [], correct_answer: '' } }));
    } else if (type === 'creative') {
      setLesson(prev => ({ ...prev, creative_block: { content: '' } }));
    }
  };

  const removeBlock = (type, index) => {
    if (type === 'guided_practice') {
      setLesson(prev => ({
        ...prev,
        guided_practice_blocks: prev.guided_practice_blocks.filter((_, i) => i !== index)
      }));
    } else if (type === 'reinforcement') {
      setLesson(prev => ({
        ...prev,
        reinforcement_blocks: prev.reinforcement_blocks.filter((_, i) => i !== index)
      }));
    } else if (type === 'challenge') {
      setLesson(prev => ({ ...prev, challenge_block: null }));
    } else if (type === 'creative') {
      setLesson(prev => ({ ...prev, creative_block: null }));
    }
  };

  const updateBlock = (type, index, newBlock) => {
    if (type === 'guided_practice') {
      setLesson(prev => ({
        ...prev,
        guided_practice_blocks: prev.guided_practice_blocks.map((b, i) => i === index ? newBlock : b)
      }));
    } else if (type === 'reinforcement') {
      setLesson(prev => ({
        ...prev,
        reinforcement_blocks: prev.reinforcement_blocks.map((b, i) => i === index ? newBlock : b)
      }));
    } else if (type === 'challenge') {
      setLesson(prev => ({ ...prev, challenge_block: newBlock }));
    } else if (type === 'creative') {
      setLesson(prev => ({ ...prev, creative_block: newBlock }));
    }
  };

  const toggleSkillTag = (tag) => {
    setLesson(prev => ({
      ...prev,
      skill_tags: prev.skill_tags.includes(tag)
        ? prev.skill_tags.filter(t => t !== tag)
        : [...prev.skill_tags, tag]
    }));
  };

  const saveLesson = async (publish = false) => {
    if (!lesson.title.trim()) {
      toast.error('Please enter a lesson title');
      return;
    }
    
    setSaving(true);
    try {
      const lessonData = { ...lesson, is_published: publish };
      await lessonBuilderAPI.createLesson(lessonData);
      toast.success(publish ? 'Lesson published!' : 'Lesson saved as draft');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving lesson:', error);
      toast.error('Failed to save lesson');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white text-xl">Loading Lesson Builder...</div>
      </div>
    );
  }

  const RealmIcon = REALM_ICONS[lesson.realm] || Star;

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-philosopher font-bold">Lesson Builder</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => saveLesson(false)}
              disabled={saving}
              className="px-4 py-2 bg-slate-700/50 hover:bg-slate-700 rounded-lg flex items-center gap-2 transition-all disabled:opacity-50"
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4" />
              Save Draft
            </button>
            <button
              onClick={() => saveLesson(true)}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg flex items-center gap-2 transition-all disabled:opacity-50"
              data-testid="publish-btn"
            >
              <Eye className="w-4 h-4" />
              Publish
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-indigo-400" />
                Lesson Details
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Title</label>
                  <input
                    type="text"
                    value={lesson.title}
                    onChange={(e) => setLesson(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter lesson title..."
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 outline-none"
                    data-testid="lesson-title-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Description</label>
                  <textarea
                    value={lesson.description}
                    onChange={(e) => setLesson(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of what students will learn..."
                    className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 outline-none resize-none"
                    rows={2}
                    data-testid="lesson-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Temple Realm</label>
                    <select
                      value={lesson.realm}
                      onChange={(e) => setLesson(prev => ({ ...prev, realm: e.target.value }))}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 outline-none"
                      data-testid="realm-select"
                    >
                      {Object.entries(realms).map(([key, realm]) => (
                        <option key={key} value={key}>{realm.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Duration (minutes)</label>
                    <input
                      type="number"
                      value={lesson.target_duration_minutes}
                      onChange={(e) => setLesson(prev => ({ ...prev, target_duration_minutes: parseInt(e.target.value) || 5 }))}
                      min={1}
                      max={15}
                      className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Discovery Block */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                Discovery (Introduction)
              </h2>
              <QuestionBlock
                block={lesson.discovery_block}
                onChange={(block) => setLesson(prev => ({ ...prev, discovery_block: block }))}
                onRemove={() => {}}
                blockType="discovery"
              />
            </div>

            {/* Guided Practice Blocks */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-400" />
                  Guided Practice
                </h2>
                <button
                  onClick={() => addBlock('guided_practice')}
                  className="px-3 py-1 bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 rounded-lg text-sm flex items-center gap-1"
                  data-testid="add-guided-practice-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Question
                </button>
              </div>
              {lesson.guided_practice_blocks.map((block, i) => (
                <QuestionBlock
                  key={i}
                  block={block}
                  onChange={(newBlock) => updateBlock('guided_practice', i, newBlock)}
                  onRemove={() => removeBlock('guided_practice', i)}
                  blockType="guided_practice"
                />
              ))}
              {lesson.guided_practice_blocks.length === 0 && (
                <p className="text-slate-500 text-sm text-center py-4">No guided practice questions yet</p>
              )}
            </div>

            {/* Reinforcement Blocks */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-400" />
                  Reinforcement
                </h2>
                <button
                  onClick={() => addBlock('reinforcement')}
                  className="px-3 py-1 bg-green-600/30 hover:bg-green-600/50 text-green-300 rounded-lg text-sm flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Question
                </button>
              </div>
              {lesson.reinforcement_blocks.map((block, i) => (
                <QuestionBlock
                  key={i}
                  block={block}
                  onChange={(newBlock) => updateBlock('reinforcement', i, newBlock)}
                  onRemove={() => removeBlock('reinforcement', i)}
                  blockType="reinforcement"
                />
              ))}
              {lesson.reinforcement_blocks.length === 0 && (
                <p className="text-slate-500 text-sm text-center py-4">No reinforcement questions yet</p>
              )}
            </div>

            {/* Challenge Block */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-orange-400" />
                  Challenge
                </h2>
                {!lesson.challenge_block && (
                  <button
                    onClick={() => addBlock('challenge')}
                    className="px-3 py-1 bg-orange-600/30 hover:bg-orange-600/50 text-orange-300 rounded-lg text-sm flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Challenge
                  </button>
                )}
              </div>
              {lesson.challenge_block ? (
                <QuestionBlock
                  block={lesson.challenge_block}
                  onChange={(newBlock) => updateBlock('challenge', 0, newBlock)}
                  onRemove={() => removeBlock('challenge', 0)}
                  blockType="challenge"
                />
              ) : (
                <p className="text-slate-500 text-sm text-center py-4">No challenge question yet</p>
              )}
            </div>

            {/* Creative Block */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  Creative Activity
                </h2>
                {!lesson.creative_block && (
                  <button
                    onClick={() => addBlock('creative')}
                    className="px-3 py-1 bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 rounded-lg text-sm flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Activity
                  </button>
                )}
              </div>
              {lesson.creative_block ? (
                <QuestionBlock
                  block={lesson.creative_block}
                  onChange={(newBlock) => updateBlock('creative', 0, newBlock)}
                  onRemove={() => removeBlock('creative', 0)}
                  blockType="creative"
                />
              ) : (
                <p className="text-slate-500 text-sm text-center py-4">No creative activity yet</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Templates */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4 text-slate-300">Quick Templates</h3>
              <div className="space-y-2">
                {templates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => applyTemplate(template)}
                    className="w-full text-left p-3 bg-slate-900/50 hover:bg-slate-800/50 rounded-xl transition-all"
                    data-testid={`template-${template.id}`}
                  >
                    <div className="font-medium text-white text-sm">{template.name}</div>
                    <div className="text-xs text-slate-400 mt-1">{template.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Skill Tags */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4 text-slate-300">Skill Tags</h3>
              <div className="flex flex-wrap gap-2">
                {skillTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => toggleSkillTag(tag)}
                    className={`px-3 py-1 rounded-full text-xs transition-all ${
                      lesson.skill_tags.includes(tag)
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Difficulty Tiers */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4 text-slate-300">Adaptive Tiers</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lesson.supports_early_explorer}
                    onChange={(e) => setLesson(prev => ({ ...prev, supports_early_explorer: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <div className="text-sm text-white">Early Explorer</div>
                    <div className="text-xs text-slate-400">Ages 6-7, K-1</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lesson.supports_pathfinder}
                    onChange={(e) => setLesson(prev => ({ ...prev, supports_pathfinder: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <div className="text-sm text-white">Pathfinder</div>
                    <div className="text-xs text-slate-400">Ages 8-9, Grade 2-3</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={lesson.supports_astronomer}
                    onChange={(e) => setLesson(prev => ({ ...prev, supports_astronomer: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <div className="text-sm text-white">Astronomer</div>
                    <div className="text-xs text-slate-400">Ages 10-12, Grade 4-6</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Preview Card */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4 text-slate-300">Preview</h3>
              <div 
                className="p-4 rounded-xl border transition-all"
                style={{ 
                  borderColor: realms[lesson.realm]?.color || '#818CF8',
                  backgroundColor: `${realms[lesson.realm]?.color || '#818CF8'}10`
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <RealmIcon className="w-4 h-4" style={{ color: realms[lesson.realm]?.color }} />
                  <span className="text-xs text-slate-400">{realms[lesson.realm]?.name}</span>
                </div>
                <h4 className="font-semibold text-white">{lesson.title || 'Untitled Lesson'}</h4>
                <p className="text-xs text-slate-400 mt-1">{lesson.description || 'No description'}</p>
                <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                  <span>{lesson.target_duration_minutes} min</span>
                  <span>•</span>
                  <span>{lesson.guided_practice_blocks.length + lesson.reinforcement_blocks.length + (lesson.challenge_block ? 1 : 0)} questions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonBuilderPage;
